import React from 'react';
import { Info, Smartphone, Boxes, Database, Layers, Users, Zap, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией об AR-технологиях и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё об AR-технологиях: типы, применение, разработка
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-indigo-500" />
          Что такое дополненная реальность (AR)?
        </h3>
        <p>
          <strong>Дополненная реальность (AR)</strong> — технология, которая накладывает цифровой контент на реальный мир через камеру устройства. В отличие от виртуальной реальности (VR), AR не погружает пользователя полностью в цифровую среду, а дополняет его восприятие реального мира цифровыми объектами, информацией или эффектами.
        </p>

        <h3 className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-indigo-500" />
          Основные типы AR-решений
        </h3>
        <ul>
          <li><strong>Мобильное AR:</strong> Приложения для смартфонов и планшетов, использующие камеру устройства. Наиболее распространенный и доступный вид AR (ARKit для iOS, ARCore для Android).</li>
          <li><strong>Веб-AR:</strong> AR-опыт, доступный через веб-браузер без необходимости установки приложения. Более ограниченный функционал, но более доступен для массового пользователя.</li>
          <li><strong>AR для гарнитур:</strong> Решения для специализированных AR-очков и гарнитур (Microsoft HoloLens, Magic Leap). Обеспечивают более глубокое погружение и свободу рук.</li>
          <li><strong>Промышленное AR:</strong> Специализированные решения для производства, логистики, обучения персонала с использованием AR-инструкций, визуализации данных и т.д.</li>
          <li><strong>AR-инсталляции:</strong> Крупномасштабные проекты для выставок, музеев, торговых центров с применением проекторов, камер и сенсоров.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Boxes className="w-5 h-5 text-orange-500" />
          Ключевые компоненты AR-разработки
        </h3>
        <ul>
          <li><strong>3D-моделирование и контент:</strong> Создание 3D-объектов, анимаций и эффектов, которые будут интегрированы в реальный мир.</li>
          <li><strong>Распознавание и отслеживание:</strong> Технологии для идентификации объектов, поверхностей, лиц или определения положения в пространстве.</li>
          <li><strong>Разработка интерфейса:</strong> Создание интуитивного взаимодействия пользователя с AR-контентом.</li>
          <li><strong>Бэкенд и серверная часть:</strong> Хранение данных, обмен информацией, аналитика использования.</li>
          <li><strong>Оптимизация производительности:</strong> Обеспечение стабильной работы на различных устройствах с разными техническими характеристиками.</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Ключевые технологии в основе AR</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            AR опирается на компьютерное зрение, обработку изображений, распознавание объектов, 
            SLAM (одновременная локализация и картирование), машинное обучение и 3D-рендеринг в режиме реального времени.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Users className="w-5 h-5 text-green-500" />
          Основные сферы применения AR
        </h3>
        <p>
          AR-технологии находят применение в самых разных областях бизнеса и повседневной жизни:
        </p>
        <ul>
          <li>
            <strong>Ритейл и электронная коммерция:</strong> Виртуальная примерка одежды, предметов интерьера, косметики; интерактивные каталоги товаров.
          </li>
          <li>
            <strong>Образование:</strong> Интерактивные учебные материалы, визуализация сложных концепций, виртуальные лаборатории.
          </li>
          <li>
            <strong>Медицина:</strong> Обучение хирургов, планирование операций, визуализация анатомии в реальном времени.
          </li>
          <li>
            <strong>Промышленность:</strong> Обучение персонала, визуальные инструкции для сборки и ремонта, удаленная экспертная поддержка.
          </li>
          <li>
            <strong>Недвижимость и архитектура:</strong> Визуализация строительных проектов, виртуальные туры по недвижимости.
          </li>
          <li>
            <strong>Развлечения и игры:</strong> AR-игры, интерактивные выставки, дополненные концерты и мероприятия.
          </li>
          <li>
            <strong>Навигация:</strong> AR-указатели направления, информация о достопримечательностях, дополненные карты.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Database className="w-5 h-5 text-purple-500" />
          Этапы разработки AR-проекта
        </h3>
        <ol>
          <li><strong>Концепция и стратегия:</strong> Определение целей проекта, целевой аудитории, ключевых функций и метрик успеха.</li>
          <li><strong>Проектирование UX/UI:</strong> Создание интуитивного и привлекательного интерфейса, учитывающего особенности AR.</li>
          <li><strong>Разработка 3D-контента:</strong> Моделирование, текстурирование и анимирование 3D-объектов.</li>
          <li><strong>Программирование:</strong> Реализация основной функциональности, интеграция AR SDK, разработка бэкенда.</li>
          <li><strong>Тестирование:</strong> Проверка работоспособности на различных устройствах и в разных условиях освещения.</li>
          <li><strong>Запуск и поддержка:</strong> Выпуск приложения, сбор обратной связи, обновления и дальнейшие улучшения.</li>
        </ol>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Типичные проблемы при внедрении AR
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Завышенные ожидания и недостаточное понимание технических ограничений</li>
            <li>Недостаток опыта разработчиков в создании интуитивных AR-интерфейсов</li>
            <li>Проблемы производительности на устаревших устройствах</li>
            <li>Сложности с освещением и отслеживанием в разных условиях</li>
            <li>Недостаточная проработка реального пользовательского опыта, фокус только на "вау-эффекте"</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" />
          Определение бюджета AR-проекта
        </h3>
        <p>
          При планировании бюджета AR-проекта следует учитывать несколько ключевых факторов:
        </p>
        <ul>
          <li><strong>Сложность и масштаб:</strong> Небольшое приложение с одной-двумя функциями будет стоить значительно дешевле комплексного корпоративного решения.</li>
          <li><strong>Качество 3D-контента:</strong> Высококачественные фотореалистичные 3D-модели могут составлять значительную часть бюджета.</li>
          <li><strong>Кросс-платформенность:</strong> Поддержка нескольких платформ увеличивает стоимость разработки.</li>
          <li><strong>Интеграции:</strong> Подключение к корпоративным системам, CRM, ERP, разработка бэкенда и API.</li>
          <li><strong>Техническая сложность:</strong> Продвинутые функции распознавания и отслеживания требуют больше ресурсов на разработку.</li>
          <li><strong>Поддержка и обновления:</strong> Долгосрочное обслуживание и добавление новых функций после запуска.</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор стоимости поможет вам получить предварительную оценку бюджета вашего AR-проекта, 
          учитывая основные параметры и особенности вашего будущего приложения!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;